import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  User,
  Building2,
  Phone,
  Mail,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  {
    name: "name",
    label: "Name",
    placeholder: "Your full name",
    icon: User,
    type: "text",
    autoComplete: "name",
  },
  {
    name: "business_name",
    label: "Business Name",
    placeholder: "Company / brand",
    icon: Building2,
    type: "text",
    autoComplete: "organization",
  },
  {
    name: "phone",
    label: "Phone",
    placeholder: "(214) 555-0123",
    icon: Phone,
    type: "tel",
    autoComplete: "tel",
  },
  {
    name: "email",
    label: "Email",
    placeholder: "you@business.com",
    icon: Mail,
    type: "email",
    autoComplete: "email",
  },
];

const INITIAL = {
  name: "",
  business_name: "",
  phone: "",
  email: "",
  needs: "",
};

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const setField = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name";
    if (form.business_name.trim().length < 2)
      e.business_name = "Please enter your business";
    if (!/^[\d\s().+\-]{7,25}$/.test(form.phone.trim()))
      e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Enter a valid email";
    if (form.needs.trim().length < 10)
      e.needs = "Tell us a little more (10+ chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        needs: form.needs.trim(),
      });
      setSuccess(true);
      toast.success("Got it — we'll be in touch within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Try again or email erik@corescaleagency.com.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact-form"
      className="relative section-padding"
      data-testid="contact-form-section"
    >
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(57,255,20,0.25)",
            color: "#fff",
          },
        }}
      />

      <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
      <div className="absolute left-1/2 top-20 w-[600px] h-[600px] -translate-x-1/2 bg-[#39FF14]/8 blur-[160px] rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Intro */}
          <div className="lg:col-span-5 reveal lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
              <span className="w-6 h-px bg-[#39FF14]" />
              Or Send A Message
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-white">
              Prefer to write{" "}
              <span className="gradient-text-green">instead?</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Tell us about your business and what you'd like to automate. We'll
              respond personally within 24 hours — no sales pitch, just a clear
              next step.
            </p>

            <ul className="mt-7 space-y-3 text-sm text-zinc-400">
              {[
                "Replies from a real engineer · not a sales rep",
                "Concrete recommendations on the first reply",
                "Your data stays private · never sold or shared",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Form card */}
          <div className="lg:col-span-7 reveal">
            <div
              className="relative rounded-3xl glass-strong p-6 sm:p-8 overflow-hidden"
              style={{
                boxShadow:
                  "0 30px 80px rgba(0,0,0,0.45), 0 0 60px rgba(57,255,20,0.05)",
              }}
            >
              <div className="absolute -top-24 -right-24 w-60 h-60 bg-[#39FF14]/15 blur-[120px] rounded-full pointer-events-none" />

              {success ? (
                <SuccessState onReset={() => setSuccess(false)} />
              ) : (
                <form
                  onSubmit={onSubmit}
                  noValidate
                  className="relative space-y-5"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    {FIELDS.map((f) => (
                      <Field
                        key={f.name}
                        field={f}
                        value={form[f.name]}
                        error={errors[f.name]}
                        onChange={(v) => setField(f.name, v)}
                      />
                    ))}
                  </div>

                  <TextAreaField
                    label="What do you need help with?"
                    placeholder="A few sentences on your business, current bottlenecks, and what you'd like to automate or build."
                    icon={MessageSquare}
                    value={form.needs}
                    error={errors.needs}
                    onChange={(v) => setField("needs", v)}
                    name="needs"
                  />

                  <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-[11px] text-zinc-500">
                      By submitting, you agree we may contact you about your
                      inquiry.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#39FF14] text-black font-semibold disabled:opacity-60 disabled:cursor-not-allowed hover:bg-white hover:shadow-[0_0_32px_rgba(57,255,20,0.45)] transition-all"
                      data-testid="contact-form-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Field = ({ field, value, error, onChange }) => {
  const { name, label, placeholder, icon: Icon, type, autoComplete } = field;
  return (
    <div>
      <label
        htmlFor={`cf-${name}`}
        className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
      >
        {label}
      </label>
      <div
        className={`mt-2 flex items-center gap-3 rounded-xl border bg-white/[0.02] px-3.5 py-3 transition-all focus-within:border-[#39FF14]/40 focus-within:bg-white/[0.04] ${
          error ? "border-red-500/40" : "border-white/10"
        }`}
      >
        <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
        <input
          id={`cf-${name}`}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
          data-testid={`contact-input-${name}`}
        />
      </div>
      {error && (
        <p className="mt-1.5 text-[11px] text-red-400" data-testid={`error-${name}`}>
          {error}
        </p>
      )}
    </div>
  );
};

const TextAreaField = ({ label, placeholder, icon: Icon, value, error, onChange, name }) => (
  <div>
    <label
      htmlFor={`cf-${name}`}
      className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
    >
      {label}
    </label>
    <div
      className={`mt-2 flex gap-3 rounded-xl border bg-white/[0.02] px-3.5 py-3 transition-all focus-within:border-[#39FF14]/40 focus-within:bg-white/[0.04] ${
        error ? "border-red-500/40" : "border-white/10"
      }`}
    >
      <Icon className="w-4 h-4 text-zinc-500 shrink-0 mt-1" />
      <textarea
        id={`cf-${name}`}
        rows={4}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 resize-none"
        data-testid={`contact-input-${name}`}
      />
    </div>
    {error && (
      <p className="mt-1.5 text-[11px] text-red-400" data-testid={`error-${name}`}>
        {error}
      </p>
    )}
  </div>
);

const SuccessState = ({ onReset }) => (
  <div
    className="relative py-10 text-center"
    data-testid="contact-form-success"
  >
    <div className="mx-auto w-14 h-14 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 flex items-center justify-center text-[#39FF14] glow-green">
      <CheckCircle2 className="w-7 h-7" />
    </div>
    <h3 className="mt-5 text-2xl font-semibold text-white">
      Message received.
    </h3>
    <p className="mt-3 text-zinc-400 max-w-md mx-auto text-sm leading-relaxed">
      Thanks for reaching out. A real human will reply within 24 hours with
      concrete next steps for your business.
    </p>
    <button
      onClick={onReset}
      className="mt-6 text-sm text-[#39FF14] hover:underline"
      data-testid="contact-form-reset"
    >
      Send another message
    </button>
  </div>
);
